/**
 * The one description of what a button looks like.
 *
 * **§9.3.** The primary button was written **fourteen** different ways across sixty-three ported files —
 * `font-bold` beside `font-semibold`, `disabled:opacity-50` beside `disabled:opacity-40`,
 * `hover:opacity-90` beside `hover:bg-primary/90` beside `hover:shadow-lg hover:-translate-y-0.5`. On
 * **twenty** of those pages two or three of them sit on the same screen, which is the version a user
 * actually notices: two primary buttons side by side, one heavier than the other, fading differently when
 * they go disabled.
 *
 * These are the *appearance* classes only — colour, weight, hover, disabled. Layout stays at the call site,
 * because a full-width button in a dialog footer and a small one in a table row are genuinely different
 * shapes and pretending otherwise would need a size prop for every one of them.
 *
 * A component would be the other way to do this, and it is the wrong one for a parity port: every one of
 * these buttons already carries its own icon, spinner, `data-*` hook and disabled logic, and rewriting a
 * hundred call sites structurally would churn every page for no visual change. This normalises what was
 * visibly inconsistent and leaves the markup alone.
 */

/** Filled, brand-coloured. The action a screen exists for. */
export const BUTTON_PRIMARY =
  'bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50'

/** Outlined. Everything beside the primary action. */
export const BUTTON_SECONDARY =
  'bg-card border border-border text-foreground font-semibold hover:bg-muted/50 transition-colors disabled:opacity-50'

/** Destructive, and it should look like it. */
export const BUTTON_DANGER =
  'bg-red-500 text-white font-bold hover:bg-red-600 transition-colors disabled:opacity-50'

/** Text-only — a tertiary action that must not compete with the two above. */
export const BUTTON_GHOST =
  'text-muted-foreground font-semibold hover:text-foreground transition-colors disabled:opacity-50'
